import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SparkLogo from './SparkLogo';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import AuthModal from './AuthModal';

const Nav = styled.nav<{ $isScrolled: boolean; $isMobileMenuOpen: boolean }>`
  padding: 0.4375rem 2rem;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: margin 0.2s ease, width 0.2s ease, background 0.2s ease, border-radius 0.2s ease;
  background: ${props => props.$isMobileMenuOpen ? 'rgb(13, 25, 48)' : 'transparent'};
  
  ${props => props.$isScrolled && !props.$isMobileMenuOpen && `
    backdrop-filter: blur(12px);
    background: rgba(13, 25, 48, 0.5);
    border: 1px solid rgba(58, 183, 255, 0.2);
    border-radius: 24px;
    margin: 1rem auto;
    width: min(calc(100% - 4rem), 1400px);
    box-shadow: 0px 5px 18px rgba(58, 183, 255, 0.08);
  `}

  @media (max-width: 768px) {
    padding: 0.4375rem 1rem;
    ${props => props.$isScrolled && !props.$isMobileMenuOpen && `
      margin: 0.5rem auto;
      width: calc(100% - 2rem);
    `}
    
    ${props => props.$isMobileMenuOpen && `
      margin: 0;
      width: 100%;
      border-radius: 0;
      border: none;
      box-shadow: none;
    `}
  }
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0.5rem 1rem;

  @media (max-width: 768px) {
    grid-template-columns: auto 1fr auto;
    padding: 0.5rem;
    gap: 1rem;
  }
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2.75rem;

  &:last-child {
    justify-self: end;
    gap: 0.75rem;
    padding-right: 0.5rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link) <{ $isScrolled: boolean }>`
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  .dropdown-arrow {
    transition: transform 0.2s ease;
  }

  &:hover {
    color: rgba(58, 183, 255, 1);
    transform: translateY(-1px);
  }
`;

const MenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  padding: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
      width: 24px;
      height: 24px;
      transition: transform 0.3s ease;
      transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'none'};
    }
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgb(13, 25, 48);
    padding: 1.5rem 1rem;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transition: transform 0.3s ease, opacity 0.2s ease, visibility 0s ${props => props.$isOpen ? '0s' : '0.3s'};
    z-index: 1001;
    overflow-y: auto;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0 0.5rem;
`;

const MobileMenuSection = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const MobileMenuButton = styled.button<{ $isExpanded?: boolean }>`
  width: 100%;
  text-align: left;
  padding: 1.25rem 0.5rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.2s ease;
    transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  &:hover {
    color: white;
  }
`;

const MobileSubMenu = styled.div<{ $isExpanded: boolean }>`
  padding-left: 1.25rem;
  height: ${props => props.$isExpanded ? 'auto' : '0'};
  opacity: ${props => props.$isExpanded ? 1 : 0};
  visibility: ${props => props.$isExpanded ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  overflow: hidden;
`;

const MobileSubMenuItem = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 1rem 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.2s ease;

  strong {
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 1rem;
    opacity: 0.7;
    line-height: 1.4;
  }

  &:hover {
    color: white;
  }
`;

const MobileNavDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.5rem 0;
`;

const MobileCTAGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 2rem 0.5rem;
  margin-top: auto;
`;

const CTAButton = styled.a`
  position: relative;
  padding: 10px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
  text-decoration: none;
  transition: all 0.2s ease;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.primary {
    position: relative;
    background: linear-gradient(90deg, #0693DB 0%, #0351AD 43%, #032040 82%);
    color: white;
    border-radius: 8px;
    padding: 10px 20px;
    font-weight: 600;
    margin-left: 0.75rem;
    
    &::before {
      content: '';
      position: absolute;
      inset: -1px;
      background: linear-gradient(90deg, #3AB7FF, #3A6BFF);
      border-radius: 9px;
      z-index: -1;
      transition: opacity 0.2s ease;
      opacity: 0;
    }

    &:hover {
      transform: translateY(-1px);
      &::before {
        opacity: 1;
      }
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-1px);
    }
  }
`;

const DropdownContent = styled.div`
  position: fixed;
  top: 5rem;
  left: 50%;
  transform: translateX(-50%) translateY(5px);
  background: rgba(13, 25, 48, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(58, 183, 255, 0.2);
  border-radius: 16px;
  padding: 0;
  width: min(800px, calc(100vw - 4rem));
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: grid;
  grid-template-columns: 320px 1fr;
  overflow: hidden;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 0;
    right: 0;
    height: 5px;
    background: transparent;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 2rem);
    grid-template-columns: 1fr;
    top: 4rem;
  }
`;

const DropdownMenuSection = styled.div`
  padding: 1rem;
  display: grid;
  gap: 0.5rem;
`;

const DropdownFeaturedSection = styled.div`
  position: relative;
  background: rgb(13, 25, 48);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 300px;
  padding: 1rem;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -50%;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.15) 0%, rgba(13, 25, 48, 0) 50%);
    animation: pulse 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(58, 183, 255, 0.1) 0%, rgba(13, 25, 48, 0) 30%),
      radial-gradient(circle at 80% 50%, rgba(58, 183, 255, 0.1) 0%, rgba(13, 25, 48, 0) 30%);
    filter: blur(20px);
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.5); opacity: 0.8; }
  }
`;

const FeaturedContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-family: 'Source Sans Pro', 'Source Sans 3', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    margin: 0;
    line-height: 1;
    text-transform: uppercase;
    color: white;
    text-align: center;
    width: 100%;
    max-width: 580px;
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .main-text {
      display: block;
      font-size: 4.5rem;
      font-weight: 800;
      letter-spacing: -0.02em;
      margin-bottom: 1.5rem;
      color: white;
    }
    
    .sub-text {
      display: flex;
      justify-content: center;
      width: 100%;
      font-size: 2.5rem;
      font-weight: 700;
      letter-spacing: 0.01em;
      color: white;
      gap: 1rem;
      
      span.word {
        display: flex;
        overflow: visible;
      }

      .letter {
        display: inline-block;
        transform-origin: 50% 100%;
        animation: letterAppear 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        opacity: 0;
        transform: scale(0.5) translateY(20px);
        letter-spacing: 0.01em;
      }

      .word:first-child .letter {
        animation-delay: calc(0.05s * var(--index));
      }

      .word:last-child .letter {
        animation-delay: calc(0.05s * (var(--index) + 8));
      }
    }
  }

  @keyframes letterAppear {
    0% {
      opacity: 0;
      transform: scale(0.5) translateY(20px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const DropdownWrapper = styled.div`
  position: static;
  display: inline-block;

  &:hover ${DropdownContent} {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  &:hover .dropdown-arrow {
    transform: rotate(180deg);
  }
`;

const DropdownItem = styled(Link)`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 1rem;
  padding: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.2s ease;
  align-items: center;

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(58, 183, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    svg {
      width: 24px;
      height: 24px;
      color: rgba(58, 183, 255, 0.9);
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  strong {
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
  }

  span {
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
  }

  &:hover {
    background: rgba(58, 183, 255, 0.1);
    
    .icon {
      background: rgba(58, 183, 255, 0.2);
      transform: scale(1.05);
    }
    
    strong {
      color: rgba(58, 183, 255, 1);
    }
  }
`;

/* Profile dropdown styled components */
const ProfileWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const ProfileAvatar = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3AB8FF, #1e90ff);
  border: 2px solid rgba(58, 183, 255, 0.4);
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;

  &:hover {
    transform: scale(1.08);
    border-color: rgba(58, 183, 255, 0.8);
    box-shadow: 0 0 12px rgba(58, 183, 255, 0.3);
  }
`;

const ProfileDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  width: 260px;
  background: rgba(20, 25, 40, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(58, 183, 255, 0.15);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  padding: 0.5rem;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
  z-index: 1001;
`;

const ProfileEmail = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  color: #8b949e;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProfileMenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.15s;
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
    color: #8b949e;
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(58, 183, 255, 0.1);
    color: #3AB8FF;

    svg {
      color: #3AB8FF;
    }
  }
`;

const ProfileMenuLink = styled(Link)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.15s;
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
    color: #8b949e;
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(58, 183, 255, 0.1);
    color: #3AB8FF;

    svg {
      color: #3AB8FF;
    }
  }
`;

const ProfileDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 0.25rem 0;
`;

const SignOutItem = styled(ProfileMenuItem)`
  color: #f85149;

  svg {
    color: #f85149;
  }

  &:hover {
    background: rgba(248, 81, 73, 0.1);
    color: #f85149;

    svg {
      color: #f85149;
    }
  }
`;

const SignInButton = styled.button`
  padding: 8px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.3);
    color: white;
  }
`;

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      setIsScrolled(currentScroll > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setExpandedSection(null);
  };

  const getUserInitial = () => {
    if (!user?.email) return '?';
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <Nav $isScrolled={isScrolled} $isMobileMenuOpen={isMenuOpen}>
      <NavContainer>
        <MenuButton
          $isOpen={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {isMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </MenuButton>
        <Link to="/" onClick={closeMenu}>
          <SparkLogo />
        </Link>
        <NavGroup>
          <DropdownWrapper>
            <NavLink to="/ai" $isScrolled={isScrolled}>
              {t("AI")}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="dropdown-arrow"
                style={{ display: 'inline', verticalAlign: 'middle' }}
              >
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </NavLink>
            <DropdownContent>
              <DropdownMenuSection>
                <DropdownItem to="/homeschool">
                  <div className="icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <div className="content">
                    <strong>{t("For Homeschooling")}</strong>
                    <span>{t("Flexible AI-powered learning for home education")}</span>
                  </div>
                </DropdownItem>
              </DropdownMenuSection>
              <DropdownFeaturedSection>
                <FeaturedContent>
                  <h2>
                    <span className="main-text">EDUCATION</span>
                    <div className="sub-text">
                      <span className="word">
                        {'WITHOUT'.split('').map((letter, index) => (
                          <span
                            key={index}
                            className="letter"
                            style={{ '--index': index } as React.CSSProperties}
                          >
                            {letter}
                          </span>
                        ))}
                      </span>
                      <span className="word">
                        {'LIMITS'.split('').map((letter, index) => (
                          <span
                            key={index}
                            className="letter"
                            style={{ '--index': index } as React.CSSProperties}
                          >
                            {letter}
                          </span>
                        ))}
                      </span>
                    </div>
                  </h2>
                </FeaturedContent>
              </DropdownFeaturedSection>
            </DropdownContent>
          </DropdownWrapper>
          <NavLink to="/features" $isScrolled={isScrolled}>{t("Features")}</NavLink>
          <NavLink to="/resources" $isScrolled={isScrolled}>{t("Resources")}</NavLink>
          <NavLink to="/about" $isScrolled={isScrolled}>{t("About")}</NavLink>
          <NavLink to="/pricing" $isScrolled={isScrolled}>{t("Pricing")}</NavLink>
        </NavGroup>
        <NavGroup>
          {user ? (
            <ProfileWrapper ref={profileRef}>
              <ProfileAvatar onClick={() => setIsProfileOpen(!isProfileOpen)}>
                {getUserInitial()}
              </ProfileAvatar>
              <ProfileDropdown $isOpen={isProfileOpen}>
                <ProfileEmail>{user.email}</ProfileEmail>
                <ProfileMenuLink to="/account" onClick={() => setIsProfileOpen(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Manage Subscription
                </ProfileMenuLink>
                <ProfileMenuItem as="a" href="https://app.sparkskool.com" target="_blank" rel="noopener noreferrer" onClick={() => setIsProfileOpen(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  {t("My Dashboard")}
                </ProfileMenuItem>
                <ProfileDivider />
                <SignOutItem onClick={() => { logout(); setIsProfileOpen(false); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t("Sign Out")}
                </SignOutItem>
              </ProfileDropdown>
            </ProfileWrapper>
          ) : (
            <>
              <SignInButton onClick={() => setIsAuthModalOpen(true)}>
                {t("Sign In")}
              </SignInButton>
              <CTAButton href="https://app.sparkskool.com" className="primary">
                {t("Start the Beta")}
              </CTAButton>
            </>
          )}
        </NavGroup>
      </NavContainer>

      <MobileMenu $isOpen={isMenuOpen}>
        <MobileMenuHeader>
          <Link to="/" onClick={closeMenu}>
            <SparkLogo />
          </Link>
          <MenuButton
            $isOpen={true}
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </MenuButton>
        </MobileMenuHeader>

        <MobileMenuSection>
          <MobileMenuButton
            onClick={() => toggleSection('solutions')}
            $isExpanded={expandedSection === 'solutions'}
            aria-expanded={expandedSection === 'solutions'}
          >
            {t("AI")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </MobileMenuButton>
          <MobileSubMenu $isExpanded={expandedSection === 'solutions'}>
            <MobileSubMenuItem to="/homeschool" onClick={closeMenu}>
              <strong>{t("For Homeschooling")}</strong>
              <span>{t("Flexible AI-powered learning")}</span>
            </MobileSubMenuItem>
          </MobileSubMenu>
        </MobileMenuSection>

        <MobileMenuSection>
          <MobileMenuButton
            onClick={() => toggleSection('resources')}
            $isExpanded={expandedSection === 'resources'}
            aria-expanded={expandedSection === 'resources'}
          >
            {t("Resources")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </MobileMenuButton>
          <MobileSubMenu $isExpanded={expandedSection === 'resources'}>
            <MobileSubMenuItem to="/blog" onClick={closeMenu}>
              <strong>{t("Blog")}</strong>
              <span>{t("Latest updates and insights")}</span>
            </MobileSubMenuItem>
            <MobileSubMenuItem to="/help" onClick={closeMenu}>
              <strong>{t("Help Center")}</strong>
              <span>{t("Get support and guidance")}</span>
            </MobileSubMenuItem>
          </MobileSubMenu>
        </MobileMenuSection>

        <MobileNavDivider />

        <MobileMenuButton as={Link} to="/about" onClick={closeMenu}>
          {t("About")}
        </MobileMenuButton>

        <MobileMenuButton as={Link} to="/pricing" onClick={closeMenu}>
          {t("Pricing")}
        </MobileMenuButton>

        <MobileCTAGroup>
          {user ? (
            <>
              <div style={{ padding: '0 0.5rem', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'rgba(58, 183, 255, 0.06)', borderRadius: '12px', border: '1px solid rgba(58, 183, 255, 0.1)' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #3AB8FF, #1e90ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 }}>
                    {getUserInitial()}
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontSize: '0.8rem', color: '#8b949e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                  </div>
                </div>
              </div>
              <MobileMenuButton as={Link} to="/account" onClick={closeMenu}>
                {t("Manage Subscription")}
              </MobileMenuButton>
              <MobileMenuButton as="a" href="https://app.sparkskool.com" onClick={closeMenu}>
                {t("My Dashboard")}
              </MobileMenuButton>
              <MobileMenuButton onClick={() => { logout(); closeMenu(); }} style={{ color: '#f85149' }}>
                {t("Sign Out")}
              </MobileMenuButton>
            </>
          ) : (
            <>
              <SignInButton onClick={() => { setIsAuthModalOpen(true); closeMenu(); }} style={{ width: '100%', padding: '12px', fontSize: '14px', marginBottom: '0.5rem' }}>
                {t("Sign In")}
              </SignInButton>
              <CTAButton href="https://app.sparkskool.com" className="primary" onClick={closeMenu}>
                {t("Start the Beta")}
              </CTAButton>
            </>
          )}
        </MobileCTAGroup>
      </MobileMenu>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={() => setIsAuthModalOpen(false)}
      />
    </Nav>
  );
};

export default Navbar; 
